import { CheckOutlined, DownOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Dropdown, Menu } from 'antd';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/config/context';
import styles from '@/i18n/locale-switch.module.scss';
import {
  DjiSetName,
  DJI_SET_NAME_OPTIONS,
  getCurrentDjiSetName,
  getDjiSetNameOption,
  isSetNameSwitchVisible,
  setCurrentDjiSetName,
} from '@/infrastructure/set-name';

type SetNameSwitchProps = { size?: 'small' | 'middle' | 'large', className?: string | undefined };

export const SetNameSwitch: FC<SetNameSwitchProps> = ({ size = 'small', className }) => {
  const { t } = useTranslation();
  const config = useAppConfig();
  const currentSetName = getCurrentDjiSetName();

  const getSetNameLabel = (target: string) => t(target);

  const menu = useMemo(() => (
    <Menu
      selectedKeys={currentSetName ? [currentSetName] : []}
      onClick={({ key }) => {
        if (key === currentSetName) {
          return;
        }

        const setName = key as DjiSetName;
        setCurrentDjiSetName(setName);

        const { jumpToAfterSwitch } = getDjiSetNameOption(setName) ?? {};

        if (jumpToAfterSwitch) {
          window.location.href = jumpToAfterSwitch;
          return;
        }

        window.location.reload();
      }}
    >
      {DJI_SET_NAME_OPTIONS.map(item => (
        <Menu.Item key={item.setName}>
          <span className={styles.option}>
            <span className={styles.optionText}>
              <span className={styles.optionMain}>{getSetNameLabel(item.label)}</span>
              <span className={styles.optionSub}>{item.setName}</span>
            </span>
            {currentSetName === item.setName && <CheckOutlined className={styles.checkIcon} />}
          </span>
        </Menu.Item>
      ))}
    </Menu>
  ), [currentSetName, t]);

  if (!config.enableSetNameSwitch || !currentSetName || !isSetNameSwitchVisible()) {
    return null;
  }

  const currentOption = getDjiSetNameOption(currentSetName);

  return (
    <Dropdown
      overlay={menu}
      placement="bottomRight"
      trigger={['click']}
      overlayClassName={styles.dropdown}
    >
      <button
        type="button"
        className={[styles.localeSwitch, styles[size], className].filter(Boolean).join(' ')}
        aria-label={t('切换地域')}
      >
        <EnvironmentOutlined className={styles.globeIcon} />
        <span className={styles.currentLocale}>
          {currentOption ? getSetNameLabel(currentOption.label) : currentSetName}
        </span>
        <DownOutlined className={styles.arrowIcon} />
      </button>
    </Dropdown>
  );
};
